/**
 * Normalize a scanned barcode value to match internal bicycle codes.
 * Internal code format: modelNum(size)sequence  e.g. "4(M)11", "2(53)16"
 *
 * Scanners may output:
 *   "4(M)11"   — exact (CODE128 reads all ASCII)
 *   "4M11"     — stripped parentheses
 *   "4-M-11"   — dashes instead of parens
 *   "4 M 11"   — spaces
 *
 * Returns { normalized, stripped, variants } where:
 *   normalized — best-guess canonical form  e.g. "4(M)11"
 *   stripped   — only alphanumerics, lowercased  e.g. "4m11"
 *   variants   — array of possible code strings to search against
 */
export function normalizeBarcode(raw) {
  if (!raw) return { normalized: '', stripped: '', variants: [] };
  let code = raw.trim();

  // 1. Already in canonical form  e.g. "4(M)11"
  const canonMatch = code.match(/^(\d+)\(([^)]+)\)(\d+)$/);
  if (canonMatch) {
    const stripped = code.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    return {
      normalized: code,
      stripped,
      variants: [code, stripped],
    };
  }

  // 2. Dash format  e.g. "4-M-11" or "4-53-02"
  const dashMatch = code.match(/^(\d+)[- ]([A-Za-z0-9]+)[- ](\d+)$/);
  if (dashMatch) {
    const norm = `${dashMatch[1]}(${dashMatch[2]})${dashMatch[3]}`;
    const stripped = code.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    return {
      normalized: norm,
      stripped,
      variants: [norm, code, stripped],
    };
  }

  // 3. Stripped parens  e.g. "4M11" or "253016"
  //    Try to detect pattern: digits + alpha/digits + digits
  const strippedMatch = code.match(/^(\d+)([A-Za-z]\d*|\d{2,})(\d{2,})$/);
  if (strippedMatch) {
    const norm = `${strippedMatch[1]}(${strippedMatch[2]})${strippedMatch[3]}`;
    const stripped = code.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    return {
      normalized: norm,
      stripped,
      variants: [norm, code, stripped],
    };
  }

  // 4. Fallback — return cleaned version
  const stripped = code.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  return {
    normalized: code,
    stripped,
    variants: [code, stripped],
  };
}

/**
 * Match a scanned code against a list of bicycles.
 * Returns { exact, partial } where:
 *   exact   — bike with exact code match (or null)
 *   partial — bikes matching by sequence number (partial/fallback)
 */
export function matchBicycle(scannedCode, bicycles) {
  const { normalized, stripped, variants } = normalizeBarcode(scannedCode);

  // Build lookup helpers
  const exactMatch = bicycles.find((b) => {
    const num = b.number || '';
    const ic = b.internal_code || '';
    // Check exact match against all variants
    return variants.some(
      (v) =>
        num === v ||
        ic === v ||
        num.toLowerCase() === v.toLowerCase() ||
        ic.toLowerCase() === v.toLowerCase()
    );
  });

  if (exactMatch) return { exact: exactMatch, partial: [] };

  // Also try matching with stripped versions of the bike codes
  const strippedMatch = bicycles.find((b) => {
    const numStripped = (b.number || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    const icStripped = (b.internal_code || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    return numStripped === stripped || icStripped === stripped;
  });

  if (strippedMatch) return { exact: strippedMatch, partial: [] };

  // Fallback: extract sequence number and try partial match
  const seqMatch = normalized.match(/\)(\d+)$/) || normalized.match(/(\d{2,})$/);
  if (seqMatch) {
    const seq = seqMatch[1];
    const partial = bicycles.filter((b) => {
      const num = b.number || '';
      const seqInCode = num.match(/\)(\d+)$/);
      return seqInCode && seqInCode[1] === seq;
    });
    return { exact: null, partial };
  }

  return { exact: null, partial: [] };
}
